// src/pages/distribuicao/VendasTransportes.js
import React, { useEffect, useMemo, useState } from "react";
import { useTable, useFilters, useSortBy } from "react-table";
import { useLocation } from "react-router-dom";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// UI base JFB
import PageHeader from "pages/components/PageHeader";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// MUI
import {
  Card,
  CircularProgress,
  TextField,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";

// Helpers

// ------------------- Filtros por coluna (padrão) -------------------
function TextFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder="filtrar..."
      style={{
        width: "100%",
        padding: "6px 4px",
        border: "none",
        borderBottom: "1px solid rgba(0,0,0,0.2)",
        outline: "none",
        background: "transparent",
        fontSize: 12,
        color: "#556",
      }}
      onFocus={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.35)")}
      onBlur={(e) => (e.target.style.borderBottom = "1px solid rgba(0,0,0,0.2)")}
    />
  );
}

function numericEquals(rows, id, filterValue) {
  const raw = String(filterValue ?? "").trim();
  if (raw === "") return rows;

  const num = Number(raw.replace(",", "."));
  if (Number.isNaN(num)) return rows;

  const EPS = 0.0005;

  return rows.filter((row) => {
    const v = row.values[id];

    if (v === null || v === undefined || v === "") return false;

    const n = typeof v === "number" ? v : Number(String(v).replace(",", "."));
    if (Number.isNaN(n)) return false;

    return Math.abs(n - num) <= EPS;
  });
}

function isoDate(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function onlyDate(v) {
  if (!v) return "";
  const s = String(v);
  const idx = s.indexOf("T");
  return idx >= 0 ? s.slice(0, idx) : s;
}

function fmtNum(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(3).replace(/\.?0+$/, ""); // 43.200 -> 43.2, 27.000 -> 27
}

function fmtMoney(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(2);
}

function fmtPerc(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toFixed(2).replace(/\.?0+$/, "");
}

function sumCol(rows, field) {
  return rows.reduce((acc, r) => {
    const v = Number(r?.[field]);
    return acc + (Number.isFinite(v) ? v : 0);
  }, 0);
}

// ------------------- Persistência de filtros -------------------
const FILTERS_KEY = "tbl:transportes:filters";
const KEEP_KEY = "tbl:transportes:keep";

export default function VendasTransportes() {
  const [loading, setLoading] = useState(false);
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const distribuicaoNome = (userData?.distribuicaonome || "").trim();
  const isDistribuicao = temFuncao("Distribuição");

  const podeVerTodosTransportes =
    temFuncao("Administração") ||
    temFuncao("Informática") ||
    temFuncao("Tesouraria") ||
    temFuncao("Armazém");

  const location = useLocation();
  const [refTransporte, setRefTransporte] = useState("");

  const isAdminLike =
    temFuncao("Administração") ||
    temFuncao("Informática") ||
    temFuncao("Tesouraria") ||
    temFuncao("Distribuição") ||
    temFuncao("Armazém");
  const isComercial = temFuncao("Comercial");

  const refUser = userData?.refvendedor ? String(userData.refvendedor) : "";
  const vendedorForcado = !isAdminLike && isComercial && refUser ? refUser : "";

  const [dialogFaturaOpen, setDialogFaturaOpen] = useState(false);
  const [faturaSelecionada, setFaturaSelecionada] = useState("");
  const [detalhesFatura, setDetalhesFatura] = useState([]);
  const [loadingFatura, setLoadingFatura] = useState(false);

  const initialKeep =
    (() => {
      try {
        const v = localStorage.getItem(KEEP_KEY);
        return v === null ? true : JSON.parse(v);
      } catch {
        return true;
      }
    })() ?? true;

  const [keepFilters, setKeepFilters] = useState(initialKeep);

  const initialFilters = keepFilters
    ? (() => {
        try {
          return JSON.parse(localStorage.getItem(FILTERS_KEY) || "[]");
        } catch {
          return [];
        }
      })()
    : [];

  // Datas default: últimos 7 dias
  //   const [dataIni, setDataIni] = useState(() => {
  //     const d = new Date();
  //     d.setDate(d.getDate() - 7);
  //     return isoDate(d);
  //   });
  const [dataIni, setDataIni] = useState(() => isoDate(new Date()));
  const [dataFim, setDataFim] = useState(() => isoDate(new Date()));

  // Listas
  const [matriculas, setMatriculas] = useState([]);
  const [pessoas, setPessoas] = useState([]); // Motoristas/Ajudantes
  const [statusList, setStatusList] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [faturas, setFaturas] = useState([]);
  const [motivos, setMotivos] = useState([]);

  const [transportes, setTransportes] = useState([]);

  // Seleções
  const [matriculaSel, setMatriculaSel] = useState(null);
  const [pessoaSel, setPessoaSel] = useState(null); // Motoristas/Ajudantes
  const [statusSel, setStatusSel] = useState(null);
  const [vendedorSel, setVendedorSel] = useState(null);
  const [clienteSel, setClienteSel] = useState(null);
  const [faturaSel, setFaturaSel] = useState(null);
  const [motivoSel, setMotivoSel] = useState(null);

  const pessoasOptions = useMemo(() => {
    // Distribuição “puro”: só pode ver o próprio nome
    if (isDistribuicao && !podeVerTodosTransportes && distribuicaoNome) return [distribuicaoNome];
    return pessoas;
  }, [isDistribuicao, podeVerTodosTransportes, distribuicaoNome, pessoas]);

  // ------------------- Colunas + Tabela (react-table padrão) -------------------
  const columns = useMemo(
    () => [
      {
        Header: "",
        id: "info",
        disableFilters: true,
        disableSortBy: true,
        accessor: (r) => r.faturanumero || "",
        Cell: ({ row }) => {
          const fat = row.original?.faturanumero;
          if (!fat) return null;

          return (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                abrirDetalhesFatura(fat);
              }}
              title="Detalhes da fatura"
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          );
        },
      },
      {
        Header: "Data Prev.",
        id: "dataprevistainicio",
        accessor: (r) => onlyDate(r.dataprevistainicio),
        Filter: TextFilter,
      },
      {
        Header: "Data Fim",
        id: "datafim",
        accessor: (r) => onlyDate(r.datafim),
        Filter: TextFilter,
        Cell: ({ row }) => {
          const d = row.original?.datafim;
          const h = row.original?.horafim;

          const data = onlyDate(d);
          const hora = h ? String(h) : "";
          const texto = data || hora ? `${data}${hora ? ` ${hora}` : ""}` : "";

          if (!texto) return null;

          return (
            <Tooltip title={texto} arrow enterTouchDelay={0} leaveTouchDelay={3000}>
              <span style={{ cursor: "pointer", textDecoration: "underline dotted" }}>{data}</span>
            </Tooltip>
          );
        },
      },
      { Header: "Ref. Transp.", accessor: "reftransporte", Filter: TextFilter },

      // Se a API já devolver nomevendedor/nomecliente, usamos.
      // Caso ainda não devolva, faz fallback para refvendedor/refcliente.
      {
        Header: "Vendedor",
        id: "vendedor",
        accessor: (r) => {
          const v = r.nomevendedor ?? r.refvendedor ?? "";
          return v.replace(/\s*(TEL|TL|TLM|TM)\s*:.*$/i, "").trim();
        },
        Filter: TextFilter,
      },
      {
        Header: "Cliente",
        id: "cliente",
        accessor: (r) => r.nomecliente ?? r.refcliente ?? "",
        Filter: TextFilter,
      },

      { Header: "Fatura", accessor: "faturanumero", Filter: TextFilter },
      {
        Header: "Valor",
        accessor: "faturavalor",
        Filter: TextFilter,
        filter: "numericEquals",
      },
      {
        Header: "Motivo",
        accessor: "motivodescricao",
        Filter: TextFilter,
        Cell: ({ value }) => (
          <span title={value || ""}>
            {(value || "").length > 30 ? `${(value || "").slice(0, 30)}…` : value || ""}
          </span>
        ),
      },
      { Header: "Status", accessor: "status", Filter: TextFilter },
      { Header: "Matrícula", accessor: "matricula", Filter: TextFilter },
      { Header: "Motorista", accessor: "motorista", Filter: TextFilter },
      { Header: "Ajudante", accessor: "ajudante", Filter: TextFilter },
      {
        Header: "Peso Entregue",
        accessor: "pesobrutoentregue",
        Filter: TextFilter,
        filter: "numericEquals",
        Cell: ({ value }) => fmtNum(value),
      },

      //   {
      //     Header: "Volume Inicial",
      //     id: "volumeinicial",
      //     accessor: (r) => fmtNum(r.volumeinicial),
      //     Filter: TextFilter,
      //   },
      {
        Header: "Peso Devol.",
        accessor: "pesodevolucao",
        Filter: TextFilter,
        filter: "numericEquals",
        Cell: ({ value }) => fmtNum(value),
      },
    ],
    []
  );

  const table = useTable(
    {
      columns,
      data: transportes,
      initialState: { filters: initialFilters },
      autoResetFilters: false,
      autoResetSortBy: false,
      filterTypes: {
        numericEquals,
      },
    },
    useFilters,
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters, state } =
    table;

  useEffect(() => {
    try {
      localStorage.setItem(KEEP_KEY, JSON.stringify(keepFilters));
    } catch {}
    if (!keepFilters) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
  }, [keepFilters, setAllFilters]);

  useEffect(() => {
    if (!keepFilters) return;
    try {
      localStorage.setItem(FILTERS_KEY, JSON.stringify(state.filters || []));
    } catch {}
  }, [state.filters, keepFilters]);

  useEffect(() => {
    if (!keepFilters && (state.filters?.length || 0) > 0) {
      setAllFilters([]);
      try {
        localStorage.removeItem(FILTERS_KEY);
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transportes]);

  const montarBody = () => ({
    idutilizador: userData?.id || 0,

    dataIni,
    dataFim,
    matricula: matriculaSel,
    pessoa: pessoaSel || "",

    reftransporte: refTransporte,

    status: statusSel,

    refvendedor: vendedorForcado || vendedorSel?.ref || "",
    refcliente: clienteSel?.ref || "",

    faturanumero: faturaSel,
    motivodescricao: motivoSel,
  });

  const carregarCombos = async () => {
    setLoading(true);

    const [dados, err, meta] = await handleApi(
      api.post("Distribuicao/SelTransportesCombos", { idutilizador: userData?.id || 0 })
    );

    if (err) {
      showAlert(err, "error");
      setLoading(false);
      return;
    }

    const payload = dados || {};

    setMatriculas(Array.isArray(payload.matriculas) ? payload.matriculas : []);
    setPessoas(Array.isArray(payload.pessoas) ? payload.pessoas : []);
    setStatusList(Array.isArray(payload.status) ? payload.status : []);
    setFaturas(Array.isArray(payload.faturas) ? payload.faturas : []);
    setMotivos(Array.isArray(payload.motivos) ? payload.motivos : []);

    // ------------------ Vendedores (ref + nome) ------------------
    {
      const [rowsVend, errVend] = await handleApi(api.post("Comercial/SelVendedoresCombo"));
      if (errVend) {
        showAlert(errVend, "error");
      } else {
        const lista = Array.isArray(rowsVend) ? rowsVend : [];

        if (vendedorForcado) {
          const meu = lista.find((x) => String(x?.ref || "") === String(vendedorForcado));
          const unico = meu || { ref: vendedorForcado, nome: vendedorForcado }; // fallback seguro
          setVendedores([unico]);
          setVendedorSel(unico);
        } else {
          const todos = { ref: "", nome: "TODOS" };
          setVendedores([todos, ...lista]);
          setVendedorSel(todos);
        }
      }
    }

    // ------------------ Clientes (ref + nome) ------------------
    {
      const bodyCli = {
        // Comercial: filtra automaticamente pelos seus clientes
        // Outros: pode listar todos (refvendedor vazio)
        refvendedor: vendedorForcado || "",
        Funcoes: userData?.funcoes || [],
      };

      const [rowsCli, errCli] = await handleApi(api.post("Comercial/SelClientesCombo", bodyCli));
      if (errCli) {
        showAlert(errCli, "error");
      } else {
        const lista = Array.isArray(rowsCli) ? rowsCli : [];
        const todos = { ref: "", nome: "TODOS" };
        setClientes([todos, ...lista]);
        setClienteSel(todos);
      }
    }

    showAlert(meta?.message || "Combos carregados.", meta?.severity || "success");
    setLoading(false);
  };

  const carregarTransportes = async (overrides = null) => {
    setLoading(true);

    const body = overrides ? { ...montarBody(), ...overrides } : montarBody();
    const [dados, err, meta] = await handleApi(api.post("Distribuicao/SelVendasTransportes", body));

    if (err) {
      showAlert(err, "error");
      setLoading(false);
      return;
    }

    setTransportes(Array.isArray(dados) ? dados : []);
    showAlert(meta?.message || "OK", meta?.severity || "success");
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const qs = new URLSearchParams(location.search || "");
      const refUrl = (qs.get("reftransporte") || "").trim();

      const pad2 = (n) => String(n).padStart(2, "0");
      const toYMD = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

      const hoje = new Date();
      const dataFimGen = toYMD(hoje);
      const dataIniGen = "1900-01-01";

      // Se abrimos por reftransporte, forçar datas genéricas
      if (refUrl) {
        setRefTransporte(refUrl);
        setDataIni(dataIniGen);
        setDataFim(dataFimGen);
      }

      await carregarCombos();

      // carregar já com overrides (evita timing do setState)
      await carregarTransportes(
        refUrl
          ? {
              dataIni: dataIniGen,
              dataFim: dataFimGen,
              reftransporte: refUrl,
            }
          : null
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrirDetalhesFatura = async (faturanumero) => {
    if (!faturanumero) return;

    setFaturaSelecionada(faturanumero);
    setDialogFaturaOpen(true);
    setLoadingFatura(true);
    setDetalhesFatura([]);

    const [dados, err] = await handleApi(
      api.post("Distribuicao/SelDetalhesFaturaTransportes", {
        faturanumero,
        refvendedor: vendedorForcado || vendedorSel?.ref || "",
      })
    );

    if (err) {
      showAlert(err, "error");
      setLoadingFatura(false);
      return;
    }

    setDetalhesFatura(Array.isArray(dados) ? dados : []);
    setLoadingFatura(false);
  };

  const fecharDialogFatura = () => {
    setDialogFaturaOpen(false);
    setFaturaSelecionada("");
    setDetalhesFatura([]);
  };

  const totalPrecoFinal = sumCol(detalhesFatura, "precoliquido");
  const totalIVA = sumCol(detalhesFatura, "ivavalor");
  const totalTotal = sumCol(detalhesFatura, "precocomiva");

  return (
    <DashboardLayout>
      <DashboardNavbar />

      {/* PROGRESS GLOBAL — sempre aqui, logo após o navbar */}
      {loading && (
        <MDBox py={2} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </MDBox>
      )}

      <PageHeader />

      <MDBox pt={1}>
        <Card>
          {/* Filtros */}
          <MDBox p={2} display="flex" flexWrap="wrap" alignItems="flex-end" gap={2}>
            {/* Datas */}
            <MDBox sx={{ minWidth: 170 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Data inicial
              </MDTypography>
              <TextField
                type="date"
                size="small"
                value={dataIni}
                onChange={(e) => setDataIni(e.target.value)}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 170 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Data final
              </MDTypography>
              <TextField
                type="date"
                size="small"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            <MDBox sx={{ minWidth: 180 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Transporte (ref)
              </MDTypography>
              <TextField
                size="small"
                value={refTransporte}
                onChange={(e) => setRefTransporte(e.target.value)}
                fullWidth
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
              />
            </MDBox>

            {/* Matrícula */}
            <MDBox sx={{ minWidth: 220 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Matrícula
              </MDTypography>
              <Autocomplete
                size="small"
                options={matriculas}
                value={matriculaSel}
                onChange={(_, v) => setMatriculaSel(v)}
                getOptionLabel={(opt) => opt || ""}
                isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="TODAS"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                  />
                )}
              />
            </MDBox>

            {/* Motorista */}
            <MDBox sx={{ minWidth: 240 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Motoristas / Ajudantes
              </MDTypography>
              <Autocomplete
                options={pessoasOptions}
                value={pessoaSel}
                onChange={(e, v) => setPessoaSel(v)}
                renderInput={(params) => <TextField {...params} size="small" />}
              />
            </MDBox>

            {/* Status */}
            <MDBox sx={{ minWidth: 220 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Status
              </MDTypography>
              <Autocomplete
                size="small"
                options={statusList}
                value={statusSel}
                onChange={(_, v) => setStatusSel(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="TODOS"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                  />
                )}
              />
            </MDBox>

            {/* Vendedor */}
            <MDBox sx={{ minWidth: 220 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Vendedor
              </MDTypography>
              <Autocomplete
                size="small"
                options={vendedores}
                value={vendedorSel}
                onChange={(_, v) => setVendedorSel(v)}
                disabled={!!vendedorForcado}
                getOptionLabel={(opt) => opt?.nome || "TODOS"}
                isOptionEqualToValue={(opt, val) =>
                  String(opt?.ref || "") === String(val?.ref || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="TODOS"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />
            </MDBox>

            {/* Cliente */}
            <MDBox sx={{ minWidth: 260 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Cliente
              </MDTypography>
              <Autocomplete
                size="small"
                options={clientes}
                value={clienteSel}
                onChange={(_, v) => setClienteSel(v)}
                getOptionLabel={(opt) => opt?.nome || "TODOS"}
                isOptionEqualToValue={(opt, val) =>
                  String(opt?.ref || "") === String(val?.ref || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="TODOS"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        fontSize: 13,
                      },
                    }}
                  />
                )}
              />
            </MDBox>

            {/* Fatura */}
            <MDBox sx={{ minWidth: 220 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Fatura nº
              </MDTypography>
              <Autocomplete
                size="small"
                options={faturas}
                value={faturaSel}
                onChange={(_, v) => setFaturaSel(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="TODAS"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                  />
                )}
              />
            </MDBox>

            {/* Motivo */}
            <MDBox sx={{ minWidth: 320 }}>
              <MDTypography variant="button" sx={{ fontSize: 12, mb: 0.5 }}>
                Motivo
              </MDTypography>
              <Autocomplete
                size="small"
                options={motivos}
                value={motivoSel}
                onChange={(_, v) => setMotivoSel(v)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="TODOS"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }}
                  />
                )}
              />
            </MDBox>

            {/* Botão */}
            <MDBox sx={{ minWidth: 180, ml: "auto" }}>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                disabled={loading}
                onClick={() => carregarTransportes()}
              >
                Atualizar Análise
              </MDButton>
            </MDBox>
          </MDBox>

          {/* tabela/resultados */}
          {/* tabela/resultados */}
          <MDBox px={2} pb={2}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <MDTypography sx={{ fontSize: 12 }}>Registos: {rows.length}</MDTypography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={keepFilters}
                    onChange={(e) => setKeepFilters(e.target.checked)}
                    size="small"
                  />
                }
                label="Manter filtros"
              />
            </MDBox>

            <MDBox sx={{ height: "62vh", overflow: "auto", borderRadius: 1 }}>
              <table {...getTableProps()} style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  {headerGroups.map((hg) => (
                    <tr {...hg.getHeaderGroupProps()}>
                      {hg.headers.map((col) => (
                        <th
                          {...col.getHeaderProps(col.getSortByToggleProps())}
                          style={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            background: "#fff",
                            textAlign: "left",
                            padding: "6px 8px",
                            fontSize: 12,
                            color: "#7b809a",
                            verticalAlign: "bottom",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {col.render("Header")}
                            {col.isSorted ? (
                              col.isSortedDesc ? (
                                <span style={{ marginLeft: 6 }}>▼</span>
                              ) : (
                                <span style={{ marginLeft: 6 }}>▲</span>
                              )
                            ) : null}
                          </div>

                          {!col.disableFilters && (
                            <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
                              {col.canFilter ? col.render("Filter") : null}
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>

                <tbody {...getTableBodyProps()}>
                  {rows.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td
                            {...cell.getCellProps()}
                            style={{
                              padding: "8px 10px",
                              borderTop: "1px solid #eee",
                              fontSize: 12,
                              color: "#1a2b4c",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}

                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={columns.length} style={{ padding: 10, opacity: 0.7 }}>
                        Sem registos para os filtros atuais.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </MDBox>
          </MDBox>
        </Card>

        <Dialog open={dialogFaturaOpen} onClose={fecharDialogFatura} fullWidth maxWidth={false}>
          <DialogTitle
            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            <MDBox>
              <MDTypography variant="h6" sx={{ lineHeight: 1.2 }}>
                Fatura {faturaSelecionada}
              </MDTypography>
              <MDTypography variant="button" sx={{ fontSize: 12, opacity: 0.8 }}>
                {detalhesFatura?.[0]?.nomecliente || ""}
              </MDTypography>
            </MDBox>

            <IconButton onClick={fecharDialogFatura} size="small" title="Fechar">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {loadingFatura ? (
              <MDBox py={3} display="flex" justifyContent="center">
                <CircularProgress />
              </MDBox>
            ) : (
              <MDBox sx={{ overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      {[
                        "Doc. Origem",
                        "Ref.",
                        "Material",
                        "Fabricante",
                        "Cat.",
                        "Qtd.",
                        "Unid.",
                        "Preço Acons.",
                        "Desc. Com.",
                        "Desc. Prom.",
                        "Preço Final",
                        "IVA",
                        "Total",
                        "Centro",
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            textAlign: "left",
                            padding: "8px 8px",
                            borderBottom: "1px solid #ddd",
                            whiteSpace: "nowrap",
                            position: "sticky",
                            top: 0,
                            background: "#fff",
                            zIndex: 1,
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {detalhesFatura.map((r, idx) => (
                      <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "8px 8px", whiteSpace: "nowrap" }}>
                          {r.docref ?? ""}
                        </td>
                        <td style={{ padding: "8px 8px", whiteSpace: "nowrap" }}>
                          {r.refmaterial ?? ""}
                        </td>

                        <td style={{ padding: "8px 8px" }} title={r.nomematerial || ""}>
                          {r.nomematerial ?? ""}
                        </td>

                        <td style={{ padding: "8px 8px" }}>{r.nomefabricante ?? ""}</td>
                        <td style={{ padding: "8px 8px", whiteSpace: "nowrap" }}>
                          {r.catitemnome ?? ""}
                        </td>
                        <td style={{ padding: "8px 8px", whiteSpace: "nowrap" }}>{r.qtd ?? ""}</td>
                        <td style={{ padding: "8px 8px", whiteSpace: "nowrap" }}>
                          {r.qtdunid ?? ""}
                        </td>

                        <td style={{ padding: "8px 8px", whiteSpace: "nowrap" }}>
                          {fmtMoney(r.precoaconselhado)}
                        </td>

                        <td style={{ padding: "8px 8px", whiteSpace: "nowrap" }}>
                          {fmtMoney(r.desccomvalor)}
                          {r.desccomperc !== null &&
                          r.desccomperc !== undefined &&
                          r.desccomperc !== ""
                            ? ` (${fmtPerc(r.desccomperc)}%)`
                            : ""}
                        </td>

                        <td style={{ padding: "8px 8px", whiteSpace: "nowrap" }}>
                          {fmtMoney(r.descpromvalor)}
                          {r.descpromperc !== null &&
                          r.descpromperc !== undefined &&
                          r.descpromperc !== ""
                            ? ` (${fmtPerc(r.descpromperc)}%)`
                            : ""}
                        </td>

                        <td style={{ padding: "8px 8px", whiteSpace: "nowrap" }}>
                          {fmtMoney(r.precoliquido)}
                        </td>

                        <td style={{ padding: "8px 8px", whiteSpace: "nowrap" }}>
                          {fmtMoney(r.ivavalor)}
                          {r.ivaperc !== null && r.ivaperc !== undefined && r.ivaperc !== ""
                            ? ` (${fmtPerc(r.ivaperc)}%)`
                            : ""}
                        </td>

                        <td style={{ padding: "8px 8px", whiteSpace: "nowrap" }}>
                          {fmtMoney(r.precocomiva)}
                        </td>
                        <td style={{ padding: "8px 8px", whiteSpace: "nowrap" }}>
                          {r.nomecentro ?? ""}
                        </td>
                      </tr>
                    ))}

                    {detalhesFatura.length === 0 && (
                      <tr>
                        <td colSpan={14} style={{ padding: 10, opacity: 0.7 }}>
                          Sem linhas para esta fatura.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: "2px solid #999", background: "#fafafa" }}>
                      {/* colunas até Preço Final */}
                      <td
                        colSpan={10}
                        style={{ padding: "8px", textAlign: "right", fontWeight: 600 }}
                      >
                        Totais
                      </td>

                      {/* Preço Final */}
                      <td style={{ padding: "8px", whiteSpace: "nowrap", fontWeight: 600 }}>
                        {fmtMoney(totalPrecoFinal)}
                      </td>

                      {/* IVA */}
                      <td style={{ padding: "8px", whiteSpace: "nowrap", fontWeight: 600 }}>
                        {fmtMoney(totalIVA)}
                      </td>

                      {/* Total */}
                      <td style={{ padding: "8px", whiteSpace: "nowrap", fontWeight: 600 }}>
                        {fmtMoney(totalTotal)}
                      </td>

                      {/* Centro (coluna final, vazia) */}
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </MDBox>
            )}
          </DialogContent>

          <DialogActions>
            <MDButton color="info" onClick={fecharDialogFatura}>
              Fechar
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}
